import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay, catchError, switchMap, map, take } from 'rxjs/operators';
import {
    Season,
    SeasonChallengesResponse,
    SeasonUnclaimedRewards,
    SeasonLevel,
    SeasonLevelsResponse,
    ActiveSeasonsResponse,
    SeasonProgressResponse,
    SeasonReward,
} from '../models/season.model';
import { Rewards } from '../../reward/models/request.model';
import { endpoints } from '../../utils/endpoints';
import { Store } from '@ngrx/store';
import { selectCurrentPlayerId } from '../../profile/store/selectors/profileGeneral.selector';

@Injectable({
  providedIn: 'root'
})
export class SeasonApi {
  private http = inject(HttpClient);
  private store = inject(Store);

  constructor() {}

  fetchActiveSeasons(): Observable<ActiveSeasonsResponse> {
    return this.http.get<Season[]>(endpoints.seasons.children.getActiveSeasons()).pipe(
      map(seasons => ({ seasons })),
      catchError(() => {
       return of({ seasons: [] });
      })
    );
  }

  fetchSeasonProgress(seasonId: string, profileId?: string): Observable<SeasonProgressResponse> {
    if (profileId) {
      return this.http.get<SeasonProgressResponse>(endpoints.seasons.children.getSeasonProgress(seasonId, profileId));
    }
    return this.store.select(selectCurrentPlayerId).pipe(
      take(1),
      switchMap(id => {
        if (!id) throw new Error('Profile ID is required for season progress');
        return this.http.get<SeasonProgressResponse>(endpoints.seasons.children.getSeasonProgress(seasonId, id));
      })
    );
  }

  fetchSeasonInfo(seasonId?: string): Observable<Season> {
    if (seasonId) {
      return this.http.get<Season>(endpoints.seasons.children.getSeasonById(seasonId));
    }
    return throwError(() => new Error('Season ID is required'));
  }

  fetchSeasonChallenges(): Observable<SeasonChallengesResponse> {
    return this.store.select(selectCurrentPlayerId).pipe(
      take(1),
      switchMap(profileId => {
        if (!profileId) return of({ challenges: [] });
        return this.http.get<Rewards[]>(endpoints.challenges.children.getChallenges(profileId)).pipe(
          map(challenges => ({ challenges })),
          catchError(() => of({ challenges: [] }))
        );
      })
    );
  }

  fetchUnclaimedRewards(): Observable<SeasonUnclaimedRewards> {
    return of({ count: 0 });
  }

  fetchSeasonLevels(seasonId: string): Observable<SeasonLevelsResponse> {
    return this.http.get<SeasonLevel[]>(endpoints.seasons.children.getSeasonLevels(seasonId)).pipe(
      map(levels => ({ levels })),
      catchError((error) => throwError(() => error))
    );
  }

  claimSeasonReward(rewardId: string): Observable<void> {
    return this.store.select(selectCurrentPlayerId).pipe(
      take(1),
      switchMap(profileId => {
        if (!profileId) return of(undefined);
        return this.http.post<void>(endpoints.challenges.children.RewardsClaim(profileId), { rewardIds: [rewardId] });
      })
    );
  }

  claimChallenge(challengeId: string): Observable<void> {
    return this.store.select(selectCurrentPlayerId).pipe(
      take(1),
      switchMap(profileId => {
        if (!profileId) return of(undefined);
        return this.http.post<void>(endpoints.challenges.children.RewardsClaim(profileId), { rewardIds: [challengeId] });
      })
    );
  }

  claimAllUnclaimedRewards(): Observable<void> {
    console.log('Mock: Claiming all unclaimed rewards');
    return of(undefined).pipe(delay(500));
  }
}
